import { useCallback, useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useNavigate } from 'react-router-dom';

import { getPblCalendarEvents } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblCalendarEvent } from '@/types/pbl';
import { formatPblDisplayDate, toHongKongCalendarDateTime } from '@/utils/pblDate';
import { getPblMeetingPurposeLabel } from '@/utils/pblMeeting';
import { errorHandler } from '@/utils/toastHandler';

import { CalendarPanel, CalendarScroller, MeetingPageShell } from '../LawyerMeetingCalendar/styles';
import {
  CalendarLegend,
  LegendDot,
  LegendItem,
  EmptyText,
  EventCard,
  EventList,
  EventMeta,
  EventTitle,
  SectionHeader,
  TitleBar,
} from './styles';
import { useTranslation } from 'react-i18next';

type FullCalendarPblEvent = {
  id: string;
  title: string;
  start: string;
  end?: string;
  color: string;
  extendedProps: PblCalendarEvent;
};

const CALENDAR_EVENT_COLORS = {
  confirmed: '#2E7D32',
  pending: '#FF6D00',
} as const;

const pendingCalendarCaseStatuses = new Set(['pending_lawyer_timeslot', 'pending_appointment_confirmation']);
const pendingCalendarStatuses = new Set(['pending', 'pending_confirmation', 'pending_citizen_selection']);
const hiddenCalendarStatuses = new Set(['cancelled', 'canceled', 'declined', 'rejected']);

const calendarMonthNameKeys = [
  'pbl.pagesHardcoded.eventCalendar.monthNames.1',
  'pbl.pagesHardcoded.eventCalendar.monthNames.2',
  'pbl.pagesHardcoded.eventCalendar.monthNames.3',
  'pbl.pagesHardcoded.eventCalendar.monthNames.4',
  'pbl.pagesHardcoded.eventCalendar.monthNames.5',
  'pbl.pagesHardcoded.eventCalendar.monthNames.6',
  'pbl.pagesHardcoded.eventCalendar.monthNames.7',
  'pbl.pagesHardcoded.eventCalendar.monthNames.8',
  'pbl.pagesHardcoded.eventCalendar.monthNames.9',
  'pbl.pagesHardcoded.eventCalendar.monthNames.10',
  'pbl.pagesHardcoded.eventCalendar.monthNames.11',
  'pbl.pagesHardcoded.eventCalendar.monthNames.12',
] as const;

const calendarWeekdayKeys = [
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.0',
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.1',
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.2',
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.3',
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.4',
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.5',
  'pbl.pagesHardcoded.eventCalendar.weekdaysShort.6',
] as const;

const buildCaseDetailPath = (caseId: number | string) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const getOptionalString = (event: PblCalendarEvent, key: string) => {
  const value = (event as unknown as Record<string, unknown>)[key];

  return typeof value === 'string' ? value.trim().toLowerCase() : '';
};

const getOptionalBoolean = (event: PblCalendarEvent, key: string) => {
  const value = (event as unknown as Record<string, unknown>)[key];

  return typeof value === 'boolean' ? value : undefined;
};

const isPendingCalendarEvent = (event: PblCalendarEvent) => {
  const explicitConfirmed = getOptionalBoolean(event, 'is_confirmed') ?? getOptionalBoolean(event, 'confirmed');
  const eventStatus =
    getOptionalString(event, 'status') ||
    getOptionalString(event, 'event_status') ||
    getOptionalString(event, 'meeting_status') ||
    getOptionalString(event, 'confirmation_status');
  const eventType = String(event.type || '').toLowerCase();

  if (explicitConfirmed === false) return true;
  if (explicitConfirmed === true) return false;

  return (
    pendingCalendarStatuses.has(eventStatus) ||
    pendingCalendarCaseStatuses.has(event.case_status) ||
    eventType.includes('appointment_proposal') ||
    eventType.includes('timeslot')
  );
};

const shouldDisplayCalendarEvent = (event: PblCalendarEvent) => {
  const eventStatus =
    getOptionalString(event, 'status') ||
    getOptionalString(event, 'event_status') ||
    getOptionalString(event, 'meeting_status') ||
    getOptionalString(event, 'confirmation_status');

  return !hiddenCalendarStatuses.has(eventStatus);
};

const getCalendarEventColor = (event: PblCalendarEvent) =>
  isPendingCalendarEvent(event) ? CALENDAR_EVENT_COLORS.pending : CALENDAR_EVENT_COLORS.confirmed;

const formatCalendarLabelTime = (value: string) => {
  const timeMatch = value.match(/[T ](\d{2}):(\d{2})/);
  let hours = Number(timeMatch?.[1]);
  let minutes = timeMatch?.[2] || '00';

  if (!Number.isFinite(hours)) {
    const date = new Date(value);
    hours = date.getHours();
    minutes = String(date.getMinutes()).padStart(2, '0');
  }

  const period = hours >= 12 ? 'pm' : 'am';
  const displayHour = hours % 12 || 12;

  return `${String(displayHour).padStart(2, '0')}:${minutes}${period}`;
};

const getCalendarLabelCaseId = (event: PblCalendarEvent) => event.case_id || String(event.case_internal_id);

const getCalendarEventTitle = (event: PblCalendarEvent) =>
  `${formatCalendarLabelTime(toHongKongCalendarDateTime(event.start) || event.start)} ${getCalendarLabelCaseId(event)}`;

const mapPblEventToCalendarEvent = (event: PblCalendarEvent): FullCalendarPblEvent => ({
  id: String(event.id),
  title: getCalendarEventTitle(event),
  start: toHongKongCalendarDateTime(event.start) || event.start,
  end: toHongKongCalendarDateTime(event.end),
  color: getCalendarEventColor(event),
  extendedProps: event,
});

const PblEventCalendar = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResize();
  const [events, setEvents] = useState<PblCalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getPblCalendarEvents({});

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.eventCalendar.failedToLoadPblCalendarEvents'));
        return;
      }

      setEvents((response.data.data || []).filter(shouldDisplayCalendarEvent));
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.eventCalendar.failedToLoadPblCalendarEvents')
      );
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const calendarEvents = events.map(mapPblEventToCalendarEvent);

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <span>
            {t('pbl.pagesHardcoded.eventCalendar.home')}<strong>{t('pbl.pagesHardcoded.eventCalendar.pblEventCalendar')}</strong>
          </span>
        </TitleBar>
      }
    >
      <MeetingPageShell>
        <CalendarPanel isTablet={isTablet}>
          <CalendarScroller>
            <FullCalendar
              plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'title',
                center: '',
                right: isMobile ? 'prev,today,next' : 'prev,today,next dayGridMonth,timeGridWeek',
              }}
              buttonText={{
                today: t('pbl.pagesHardcoded.eventCalendar.today'),
                month: t('pbl.pagesHardcoded.eventCalendar.month'),
                week: t('pbl.pagesHardcoded.eventCalendar.week'),
              }}
              titleFormat={({ start }) =>
                t('pbl.pagesHardcoded.eventCalendar.monthTitle', {
                  month: t(calendarMonthNameKeys[start.month] || calendarMonthNameKeys[0]),
                  year: start.year,
                })
              }
              dayHeaderContent={(header) => t(calendarWeekdayKeys[header.date.getDay()] || calendarWeekdayKeys[0])}
              events={calendarEvents}
              displayEventTime={false}
              eventClick={(eventInfo) => {
                const selectedEvent = eventInfo.event.extendedProps as PblCalendarEvent;
                navigate(buildCaseDetailPath(selectedEvent.case_internal_id));
              }}
              dayMaxEvents={true}
              height={isMobile ? 'auto' : '760px'}
              contentHeight="auto"
              fixedWeekCount={!isMobile}
              viewClassNames="pbl-lawyer-calendar"
            />
          </CalendarScroller>
          <CalendarLegend aria-label={t('pbl.pagesHardcoded.eventCalendar.meetingTimeLegend')}>
            <LegendItem>
              <LegendDot $color={CALENDAR_EVENT_COLORS.pending} />
              <span>{t('pbl.pagesHardcoded.eventCalendar.pendingMeetingTime')}</span>
            </LegendItem>
            <LegendItem>
              <LegendDot $color={CALENDAR_EVENT_COLORS.confirmed} />
              <span>{t('pbl.pagesHardcoded.eventCalendar.confirmedMeetingTime')}</span>
            </LegendItem>
          </CalendarLegend>
        </CalendarPanel>

        <EventList>
          <SectionHeader>{t('pbl.pagesHardcoded.eventCalendar.meetings')}</SectionHeader>
          {isLoading && events.length === 0 ? <EmptyText>{t('pbl.pagesHardcoded.eventCalendar.loadingMeetings')}</EmptyText> : null}
          {!isLoading && events.length === 0 ? <EmptyText>{t('pbl.pagesHardcoded.eventCalendar.noMeetingsInThisDateRange')}</EmptyText> : null}
          {events.map((event) => (
            <EventCard key={event.id} $color={getCalendarEventColor(event)}>
              <EventTitle>{event.title}</EventTitle>
              {event.purpose ? <EventMeta>{getPblMeetingPurposeLabel(event.purpose, t)}</EventMeta> : null}
              <EventMeta>{event.case_id || t('pbl.pagesHardcoded.eventCalendar.caseValue', { value1: event.case_internal_id })}</EventMeta>
              <EventMeta>{formatPblDisplayDate(event.start)}</EventMeta>
              {event.participants_summary?.citizen_name ? (
                <EventMeta>{event.participants_summary.citizen_name}</EventMeta>
              ) : null}
              {event.participants_summary?.oc_office_name ? (
                <EventMeta>{event.participants_summary.oc_office_name}</EventMeta>
              ) : null}
              {(event.participants_summary?.lawyer_names || []).length > 0 ? (
                <EventMeta>{event.participants_summary?.lawyer_names.join(', ')}</EventMeta>
              ) : null}
            </EventCard>
          ))}
        </EventList>
      </MeetingPageShell>
    </PageContainer>
  );
};

export default PblEventCalendar;
